import '@/events/listeners';
import '@/events/listeners/mixpanel';
import '@/echo';
import { createApp } from 'vue';
import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
import { gpxPrimeVueTheme } from '@gpx-spa/ui/primevue-theme';
import '@gpx-spa/ui/primevue-overrides';
import NProgress from 'nprogress';
import store from './store';
import router from './router';
import { provideAccountSettingsSource } from '@/components/shipment/form/domain/account-defaults';
import mixpanel from 'mixpanel-browser';
import { createPinia } from 'pinia';
import { useLocalStorage } from '@vueuse/core';

import vClickOutside from 'click-outside-vue3';
import Toast from 'vue-toastification';
import {
  getAccountIdFromURL,
  getAccountRedirectionUrl,
} from '@/utils/b2b-account';
import { redirectToWithToken, getTokenFromURL } from '@/utils/token';
import {
  removeImitateTokenIfNeeded,
  storeImitateFrom,
  storeImitatingAdmin,
} from '@/utils/imitate';
import { VITE_MIXPANEL_TOKEN, VITE_VITALLY_TOKEN } from '@/config/environment';

import App from './App.vue';
import { registerComponents } from './components/register-components';
import { registerIcons } from './components/register-icons';

import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'vue-toastification/dist/index.css';
import 'nprogress/nprogress.css';
import './assets/tailwind.css';
import './assets/shipment-list-map-tooltip.css';
import './assets/overrides.css';
import './assets/heroicons.css';
import '@vuepic/vue-datepicker/dist/main.css';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import { startBugsnag } from './bugsnag';
import { exposeRouter } from './helpers';
import { init } from '@/composables/useVitally';
import { abilitiesPlugin } from '@casl/vue';
import defineAbilities from './defineAbilities';
import { initializeQueries } from '@gpx-spa/queries';
import service, { publicService } from '@/services';

const initVitally = (account, user) => {
  const imitatingAdmin = useLocalStorage('imitating_admin', null);
  if (
    !account.uses_gpx_app ||
    !account.included_in_cs ||
    imitatingAdmin.value ||
    !VITE_VITALLY_TOKEN
  ) {
    return;
  }

  init(VITE_VITALLY_TOKEN, account, user);
};

const startApp = () => {
  const bugsnagVue = startBugsnag();
  const app = createApp(App);
  const pinia = createPinia();

  provideAccountSettingsSource(() => store.getters['auth/accountSettings']);

  if (bugsnagVue) {
    app.use(bugsnagVue);
  }

  app
    .use(store)
    .use(pinia)
    .use(abilitiesPlugin, defineAbilities(), { useGlobalProperties: true })
    .use(router)
    .use(PrimeVue, {
      theme: gpxPrimeVueTheme,
    })
    .use(vClickOutside)
    .use(Toast, {
      transition: 'Vue-Toastification__fade',
      timeout: 3000,
      draggable: true,
      draggablePercent: 0.25,
    });
  exposeRouter(router);
  initializeQueries(app, service, publicService);

  registerComponents(app);
  registerIcons(app);

  app.directive('tooltip', Tooltip);

  app.config.warnHandler = () => null;

  app.config.errorHandler = (err, vm, info) => {
    if (err.message && err.message.includes('index') && err.stack && err.stack.includes('splitpanes')) {
      return; // Don't log anything, just ignore
    }

    console.error('[Global Vue Error Handler]', { err, vm, info, stack: err?.stack });
  };
  app.mount('#app');
};

const initApp = async () => {
  mixpanel.init(VITE_MIXPANEL_TOKEN, {
    autotrack: false,
    track_pageview: false,
    debug: import.meta.env.DEV,
    disable_persistence: true,
  });

  NProgress.configure({ showSpinner: false, speed: 350 });

  storeImitateFrom();
  storeImitatingAdmin();

  try {
    removeImitateTokenIfNeeded();
    const token = getTokenFromURL();

    if (token) {
      await store.dispatch('auth/evaluateTemporaryToken', {
        temporaryToken: token,
      });
    }

    const accountId = getAccountIdFromURL();
    await store.dispatch('auth/fetchMe', { accountId });
    const account = store.getters['auth/account'];
    const user = store.getters['auth/user'];
    const redirectionUrl = getAccountRedirectionUrl(account);
    defineAbilities();
    if (!redirectionUrl) {
      if (user?.id) {
        initVitally(account, user);
      }
      startApp();
      return;
    }

    const temporaryToken = await store.dispatch('auth/fetchTemporaryToken');

    window.location.replace(
      redirectToWithToken(redirectionUrl, temporaryToken, account),
    );
  } catch (e) {
    window.location.href = '/login';
  }
};


initApp();
